import { z } from "zod";
//#region src/app-open.ts
/**
* The app-open record (2026-10-21-app-open-audit, issue #4407).
*
* One client posts the app's catalog id here: `servicesFor` of the surface host. It posts once per
* routed non-root mount with a catalog id, on the shell origin and the kernel origin. A root
* mount and an embedded mount post nothing.
*
* core-api then writes one `app_opened` audit row. The row names the person as actor and the
* app's catalog id as `resourceId` (decisions D1, D2, D5). The client sends one request, and:
*
* - it does not wait for the answer;
* - it adds no retry of its own, but the origin fetch policy can send the request again after a
*   401 refresh, a cold start or a maintenance window;
* - it drops every error.
*
* So an audit write never blocks an open (D11).
*
* The route admits one caller class (D8): a signed-in person in an interactive session, on
* the shell origin or the kernel origin. That caller has a verified token with these claims:
*
* - `principalKind` absent or `'human'`;
* - none of `sessionType`, `scp`, `azp`, `chainDepth` or the delegation mark;
* - `scope: 'data_plane'`;
* - an `aud` that is the issuer string or exactly the kernel audience of the request host.
*
* Every other token gets 403 with the code
* {@link APP_OPEN_REFUSAL_CODES}`.notAnInteractivePerson` (`not_an_interactive_person`). The
* person and the org come from the verified token, never from the body.
*
* A success answers 204 with no body. Two cases get the one 403 with the code
* {@link APP_OPEN_REFUSAL_CODES}`.appNotVisible` (`app_not_visible`): an app that the person
* cannot see, and an id that names no app (D9). A publish failure answers 503
* `audit_unavailable`. A caller over the throttle gets 429 (D10). The throttle counts per
* verified `sub` and per token class (R-007 of the plan).
*/
const APP_OPEN_ROUTE = "/v1/auth/app-opens";
/**
* The request body: the catalog id of the app that opened. Strict, so a second key is a 400
* through `ZionStrictValidationPipe`. The id is the `release.apps_catalog` UUID, the value
* `AppRegistryEntry.appUuid` holds in the shell and `appTokenRequestSchema.appId` carries.
* core-api writes the id of the catalog row it matched, never this string (D9).
*/
const appOpenRequestSchema = z.object({ appId: z.string().uuid().describe("The release.apps_catalog id of the app that the signed-in person opened.") }).strict();
/**
* The refusal codes of the route, each sent as the `code` of a 403 body. One code covers an
* unknown id and an invisible app, so the answer does not tell a caller which apps exist.
*/
const APP_OPEN_REFUSAL_CODES = Object.freeze({
	notAnInteractivePerson: "not_an_interactive_person",
	appNotVisible: "app_not_visible"
});
//#endregion
export { APP_OPEN_REFUSAL_CODES, APP_OPEN_ROUTE, appOpenRequestSchema };

//# sourceMappingURL=app-open.js.map